import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Briefcase, Download, FileText, Loader2 } from "lucide-react";
import { listMyMatters } from "@/lib/api/client-matters.functions";
import { MATTER_CATEGORIES } from "@/lib/email-presets";

export const Route = createFileRoute("/_authenticated/portal/matters")({
  head: () => ({
    meta: [
      { title: "My matters & documents · NDLCS Portal" },
      {
        name: "description",
        content:
          "View the matters we are handling for you and download every document our team has sent to you.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: MyMatters,
});

function categoryLabel(value: string) {
  return MATTER_CATEGORIES.find((c) => c.value === value)?.label ?? value;
}

function MyMatters() {
  const fetchMatters = useServerFn(listMyMatters);
  const q = useQuery({ queryKey: ["my-matters"], queryFn: () => fetchMatters() });

  return (
    <div className="container-narrow space-y-5 py-6">
      <header>
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-gold">
          <Briefcase className="h-3.5 w-3.5" /> Client portal
        </p>
        <h1 className="font-display text-2xl text-navy sm:text-3xl">My matters &amp; documents</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Everything our team has worked on for you, with the documents we sent by email. We keep
          your records securely for five years in line with POPIA.
        </p>
      </header>

      {q.isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your matters…
        </p>
      )}

      {q.data && q.data.matters.length === 0 && (
        <p className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          You don&apos;t have any matters yet. Once our team works on something for you, it will
          appear here with all related documents.
        </p>
      )}

      <ul className="space-y-3">
        {(q.data?.matters ?? []).map((m: any) => (
          <li key={m.id} className="rounded-lg border border-border bg-card p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gold">
                  {categoryLabel(m.category)}
                </p>
                <h2 className="font-display text-lg text-navy">{m.title}</h2>
                <p className="text-xs text-muted-foreground">
                  Opened {new Date(m.created_at).toLocaleDateString()}
                  {m.reference ? ` · Ref ${m.reference}` : ""}
                </p>
              </div>
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] capitalize">
                {m.status}
              </span>
            </div>

            {m.documents.length > 0 && (
              <div className="mt-3 border-t border-border pt-2">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Documents
                </p>
                <ul className="space-y-1">
                  {m.documents.map((d: any) => (
                    <li key={d.id} className="flex items-center justify-between gap-2 text-sm">
                      <span className="inline-flex min-w-0 items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate">{d.name}</span>
                      </span>
                      {d.url ? (
                        <a
                          href={d.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-navy hover:underline"
                        >
                          <Download className="h-3 w-3" /> Download
                        </a>
                      ) : (
                        <span className="shrink-0 text-[11px] text-muted-foreground">
                          Sent by email
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
